import React, { useState, useEffect } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { ValidationError } from '../../components';

import css from './FieldTimeSelect.module.css';

/**
 * Splits a 12-hour or 24-hour time string into hour, minute, and period (AM/PM) components.
 *
 * @param {string} timeStr - Time string in "hh:mm A" or "HH:mm" format.
 * @returns {Object} Object containing hour, minute, and ampm keys.
 */
const parseTime12hOr24h = timeStr => {
  if (!timeStr) {
    return { hour: '', minute: '', ampm: '' };
  }

  const lowerStr = timeStr.toLowerCase();
  const hasAmPm = lowerStr.includes('am') || lowerStr.includes('pm');

  if (hasAmPm) {
    const ampm = lowerStr.includes('pm') ? 'PM' : 'AM';
    const cleanTime = timeStr.replace(/[a-zA-Z]/g, '').trim();
    if (!cleanTime.includes(':')) {
      return { hour: '', minute: '', ampm: '' };
    }
    const [hStr, mStr] = cleanTime.split(':');
    const h = String(parseInt(hStr, 10)).padStart(2, '0');
    const m = String(parseInt(mStr, 10)).padStart(2, '0');
    return { hour: h, minute: m, ampm };
  } else {
    if (!timeStr.includes(':')) {
      return { hour: '', minute: '', ampm: '' };
    }
    const [hhStr, mmStr] = timeStr.split(':');
    const hh = parseInt(hhStr, 10);
    const m = mmStr.trim();

    let ampm = 'AM';
    let h12 = hh;
    if (hh >= 12) {
      ampm = 'PM';
      if (hh > 12) {
        h12 = hh - 12;
      }
    } else if (hh === 0) {
      h12 = 12;
    }

    return {
      hour: String(h12).padStart(2, '0'),
      minute: String(parseInt(m, 10)).padStart(2, '0'),
      ampm,
    };
  }
};

/**
 * Combines 12-hour hour, minute, and period components into a 12-hour time string ("hh:mm A").
 *
 * @param {string} h12 - Hour (01-12).
 * @param {string} m - Minute (00-59).
 * @param {string} ampm - Period ("AM" or "PM").
 * @returns {string} Combined 12-hour time string ("hh:mm A") or empty string if incomplete.
 */
const formatTime12h = (h12, m, ampm) => {
  if (!h12 || !m || !ampm) return '';
  const hh = String(h12).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const period = ampm.toUpperCase();
  return `${hh}:${mm} ${period}`;
};

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const AMPM = ['AM', 'PM'];

const FieldTimeSelectComponent = props => {
  const { rootClassName, className, labelClassName, id, label, input, meta, ...rest } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { touched, invalid, error } = meta;
  const hasError = touched && invalid && error;

  const currentVal = input.value || '';
  const { hour: initialHour, minute: initialMinute, ampm: initialAmpm } = parseTime12hOr24h(currentVal);

  const [selectedHour, setSelectedHour] = useState(initialHour);
  const [selectedMinute, setSelectedMinute] = useState(initialMinute);
  const [selectedAmpm, setSelectedAmpm] = useState(initialAmpm);

  useEffect(() => {
    const { hour, minute, ampm } = parseTime12hOr24h(currentVal);
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setSelectedAmpm(ampm);
  }, [currentVal]);

  const handleChange = (type, val) => {
    let nextHour = selectedHour;
    let nextMinute = selectedMinute;
    let nextAmpm = selectedAmpm;

    if (type === 'hour') {
      nextHour = val;
      setSelectedHour(val);
    } else if (type === 'minute') {
      nextMinute = val;
      setSelectedMinute(val);
    } else if (type === 'ampm') {
      nextAmpm = val;
      setSelectedAmpm(val);
    }

    if (nextHour && nextMinute && nextAmpm) {
      const formattedValue = formatTime12h(nextHour, nextMinute, nextAmpm);
      input.onChange(formattedValue);
    } else {
      input.onChange('');
    }
  };

  const labelClasses = classNames(labelClassName || css.label);
  const classes = classNames(rootClassName || css.root, className);
  const selectClasses = classNames(css.select, {
    [css.selectError]: hasError,
  });

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={`${id}-hour`} className={labelClasses}>
          {label}
        </label>
      ) : null}

      <div className={css.selectsContainer}>
        <select
          id={`${id}-hour`}
          className={selectClasses}
          value={selectedHour}
          onChange={e => handleChange('hour', e.target.value)}
          {...rest}
        >
          <option value="">Hour</option>
          {HOURS.map(h => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>

        <select
          id={`${id}-minute`}
          className={selectClasses}
          value={selectedMinute}
          onChange={e => handleChange('minute', e.target.value)}
          {...rest}
        >
          <option value="">Min</option>
          {MINUTES.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <select
          id={`${id}-ampm`}
          className={selectClasses}
          value={selectedAmpm}
          onChange={e => handleChange('ampm', e.target.value)}
          {...rest}
        >
          <option value="">AM/PM</option>
          {AMPM.map(p => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * A composite field for selecting time using three dropdowns (Hour, Minute, AM/PM).
 * Outputs a standard "HH:mm" time string.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name - Name of the input in Final Form.
 * @param {string} props.id - Unique ID (required if label is provided).
 * @param {ReactNode} [props.label] - Label of the field.
 * @param {string} [props.className] - CSS class for root container.
 * @param {string} [props.labelClassName] - CSS class for label element.
 */
const FieldTimeSelect = props => {
  return <Field component={FieldTimeSelectComponent} {...props} />;
};

export default FieldTimeSelect;
